import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from 'react-paginate';
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useModal } from "../contexts/useModal";

import {
  IconFileImport,
  IconFileExport,
  IconFileExcel,
  IconPlus,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconAccessible,
  IconCheck,
  IconMinus,
  IconRecycle,
  IconRefresh
} from '@tabler/icons-react';
import { Box, Button, Checkbox, Flex, LoadingOverlay, Menu, ScrollArea, Select, Table, TableThead, useMantineTheme } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { WorkerUserReject } from "../components/Modal";
import { INT_MAX, setActionLog } from "../utils/util";
import { useCanWrite } from "../utils/useCanWrite";
import {
  dangerActionProps,
  labels,
  neutralActionProps,
  primaryActionProps,
} from "../constants/uiActions";

const WorkerApproval: React.FC<{ menuTitle: string }> = ({menuTitle}) => {
    const canWrite = useCanWrite();
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [length, setLength] = useState(15);
    const [pageNo, setPageNo] = useState(1);
    const [pageTotal, setPageTotal] = useState(1);
    const _isSearch = useRef(false);
    const [isSearch, setIsSearch] = useState(false);
    const todayStr = dayjs().format('YYYY-MM-DD'); // KST 기준 오늘 날짜
    const [startDate, setStartDate] = useState<string>(todayStr);
    const [endDate, setEndDate] = useState<string>(todayStr);
    const _isAction = useRef(false);
    const [isAction, setIsAction] = useState(false);
    const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>([]);
    const [role, setRole] = useState('W_INJ_ALL');

    const {showModal} = useModal();
    

    const theme = useMantineTheme();

    const lock1 = useRef(false);

  useEffect(() => {
    axiosInstance.get("/api/v1/role/permission/list",{
        params:{
          currentPageNo : 1
          ,recordsPerPage : INT_MAX
          ,workerYn : 'Y'
        }
      }).then((res) => {
      console.log(res.data.data.list);
      const mapped = res.data.data.list.map((item: any) => ({
        value: item.roles,
        label: item.roleName,
      }));
      const withDefault = [{ value: "", label: "작업유형 선택" }, ...mapped];
      setRoleOptions(withDefault);
    }).catch((err) => {
      console.log(err);
      setRoleOptions([{ value: "", label: "작업유형 선택" }]);
    });
  },[]);

    useEffect(() => {
      if (!lock1.current) {
        lock1.current = true; 
        return; 
      }
      if (pageNo !== 1) {
        setPageNo(1);
      }else{
        getList();
      }
    }, [length]);

    useEffect(() => {
      getList();
    }, [pageNo]);

    const getList = () =>{
      if(_isSearch.current)return;
      setSelectedRows([]);
      _isSearch.current = true;
      setIsSearch(true);
      axiosInstance
        .get('/api/v1/user/list',{
          params:{
            currentPageNo:1
            ,recordsPerPage:INT_MAX
            ,workerYn:'Y'
            ,approvalYn:'N'
          }
        })
        .then((res) => {
          if (res.status === 200) {
            const data = res.data as any;
            const r = data.data;
            const p = r.paging;
            const l = r.list;
            setData(l || []);
          } else {
            setData([]);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error(labels.searchFailed);
        })
        .finally(()=>{
          _isSearch.current = false;
          setIsSearch(false);
        });
    }

    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    const allSelected = data.length > 0 && selectedRows.length === data.length;
    const someSelected = selectedRows.length > 0 && !allSelected;
    
    const handleSelectAll = (checked: boolean) => {
      toast.info(checked);
      setSelectedRows(checked ? data.map((row) => row.userNo) : []);
    };

    const handleApproval = async () => {
      if(_isAction.current)return;
      _isAction.current = true;
      setIsAction(true);

      if(!role){
        toast.error('작업유형을 선택해주세요.');
        cancelIsAction();
        return;
      }

      if(selectedRows.length < 1){
        toast.error('승인하실 작업자들을 선택해주세요');
        cancelIsAction();
        return;
      }
      try{
        for (const userNo of selectedRows) {
          const r = data.find((item) => item.userNo === userNo);
          const resParam = {
            userNo:r.userNo
            ,userId:r.userId
            ,approvalYn:'Y'
            ,roleCode:role
          }
          const res = await axiosInstance.patch('/api/v1/user', resParam);
          if (res.data.code === '200') {
            await setActionLog({eventType:'UPDATE',title:'작업자 승인 성공',content:resParam});
          }else{
            await setActionLog({eventType:'UPDATE',title:'작업자 승인 실패',content:resParam});
          }
        }
        getList();
      }catch(err){
        await setActionLog({eventType:'UPDATE',title:'작업자 승인 실패',content:err});
        toast.error('승인 작업에 실패하였습니다.');
      }finally{
        cancelIsAction();
      }
    }

    const handleReject = async () => {
      if(_isAction.current)return;
      _isAction.current = true;
      setIsAction(true);

      if(selectedRows.length < 1){
        toast.error('반려하실 작업자들을 선택해주세요');
        cancelIsAction();
        return;
      }

      // 선택된 행들의 상세 정보를 가져옴
      const selectedUserData = selectedRows.map(userNo => {
        const user = data.find(item => item.userNo === userNo);
        return {
          userNo: user.userNo,
          userId: user.userId,
          userName: user.userName
        };
      });

      await showModal({component:WorkerUserReject,props:{userNoList:selectedRows, userData: selectedUserData}})
      getList();
      cancelIsAction();
    }
  
    const cancelIsAction = () => {
      _isAction.current = false;
      setIsAction(false);
    }


    const rows = data.map((row) => {
      const isSelected = selectedRows.includes(row.userNo);
      return (
        <Table.Tr
          key={row.userNo}
          onClick={() => {
            setSelectedRows((prev) => {
              if (isSelected) {
                return prev.filter((userNo) => userNo !== row.userNo);
              } else {
                const updated = [...prev, Number(row.userNo)];
                return Array.from(new Set(updated)).sort((a, b) => a - b);
              }
            });
          }}
          bg={selectedRows.includes(row.userNo) ? 'var(--mantine-color-blue-light)' : undefined}
        >
          <Table.Td>
            <Checkbox
              aria-label="Select row"
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setSelectedRows((prev) => {
                  if (checked) {
                    const updated = [...prev, Number(row.userNo)];
                    return Array.from(new Set(updated)).sort((a, b) => a - b);
                  } else {
                    return prev.filter((userNo) => userNo !== row.userNo);
                  }
                });
              }}
            />
          </Table.Td>
          <Table.Td p={'4px 12px'}>{row.userId}</Table.Td>
          <Table.Td p={'4px 12px'}>{row.userName}</Table.Td>
          <Table.Td p={'4px 12px'}>{row.createdDate}</Table.Td>
        </Table.Tr>
      );
    });

    return(
        <Flex bg={'#fafbfc'} bdrs={12} p={'0.75rem 1.5rem'} direction={'column'}>
            <Box fz={"1.75rem"} fw={700} pb={"1rem"}>{menuTitle}</Box>
            <Flex align={"flex-end"} pb={"1rem"}>
              <Flex align={"flex-end"} gap={"xs"}>
                <Button
                  leftSection={<IconRefresh size={18} stroke={1.5} />}
                  onClick={getList}
                  {...neutralActionProps}
                >{labels.refresh}</Button>
                <Select
                  label="작업유형"
                  data={roleOptions}
                  defaultValue={roleOptions[0]?.value || 'W_INJ_ALL'}
                  value={role}
                  allowDeselect={false}
                  onChange={(_value, option) => setRole(_value||'')}
                />
                <Button
                  leftSection={<IconCheck size={20} stroke={1.5} />}
                  onClick={handleApproval}
                  {...primaryActionProps}
                  disabled={!canWrite || isSearch || isAction}
                >{labels.approve}</Button>
              </Flex>
              <Flex ml={"auto"} gap={"xs"}>
                <Button
                  leftSection={<IconMinus size={20} stroke={1.5} />}
                  onClick={handleReject}
                  {...dangerActionProps}
                  disabled={!canWrite || isSearch || isAction}
                >{labels.reject}</Button>
              </Flex>
            </Flex>
            <ScrollArea h={'calc(100vh - 304px)'} pos={'relative'} bg={'#fff'} bdrs={8}>
                <LoadingOverlay
                  visible={isSearch}
                  zIndex={1000}
                  overlayProps={{ radius: 'sm', bg:'rgba(0,0,0,0.3)' }}
                  loaderProps={{ color: 'pink', type: 'bars' }}
                />
                <Table>
                  <Table.Thead>
                    <Table.Tr bg={'#f5f6fa'} ta={'left'} pos={'sticky'} top={0}>
                      <Table.Th p={12} pos={'sticky'} top={0} w={80}>
                        <Checkbox
                          aria-label="Select all rows"
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(event) => handleSelectAll(event.currentTarget.checked)}
                        />
                      </Table.Th>
                      <Table.Th p={12} pos={'sticky'} top={0} w={240}>아이디</Table.Th>
                      <Table.Th p={12} pos={'sticky'} top={0} w={240}>이름</Table.Th>
                      <Table.Th p={12} pos={'sticky'} top={0}>등록일</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows}
                    {/*Array.from({ length: 30 }).map((_, i) => (
                      <Table.Tr key={i} style={{ height: 30,}}>
                        <td colSpan={5}>asd</td>
                      </Table.Tr>
                    ))*/}
                  </Table.Tbody>
                </Table>
            </ScrollArea>
        </Flex>
    );
}

export default WorkerApproval;